/**
 * Settings → About & status route.
 *
 * Shows build/version metadata (`system.about`) and a live per-service
 * status grid (`system.health`, polled every 15s). Visible to all members.
 *
 * @see prototype/settings — settings rail baseline
 */
import { createFileRoute } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import { AboutCard, SystemStatusGrid } from "../../../../features/settings/about/index.ts";

export const Route = createFileRoute("/(workspace)/settings/about/")({
  component: AboutSettingsPage,
});

function AboutSettingsPage() {
  const about = trpc.system.about.useQuery({});
  const health = trpc.system.health.useQuery({}, { refetchInterval: 15_000 });

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "About & status" }]}>
      <SettingsShell
        activeSection="about"
        pageTitle="About & status"
        pageDescription="Version and build details for this Flint deployment, plus the live status of every backing service."
      >
        <div className="flex flex-col gap-4">
          {about.error ? (
            <Alert intent="bad" title="Couldn't load build info" description={about.error.message} />
          ) : about.data ? (
            <AboutCard info={about.data} />
          ) : (
            <p className="text-muted text-sm">Loading version…</p>
          )}

          {health.error ? (
            <Alert intent="bad" title="Couldn't load system status" description={health.error.message} />
          ) : health.data ? (
            <SystemStatusGrid
              services={health.data}
              isFetching={health.isFetching}
              lastUpdated={health.dataUpdatedAt ? new Date(health.dataUpdatedAt) : null}
              onRefresh={() => void health.refetch()}
            />
          ) : (
            <p className="text-muted text-sm">Checking services…</p>
          )}
        </div>
      </SettingsShell>
    </WorkspaceShell>
  );
}
